import { CircularProgress, Grid, Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../../graphql/magento'
import useRetailPrices from '../../../../hooks/useRetailPrices'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useUserCategory from '../../../../hooks/useUserCategory'
import { readCartDispensaryPromoCodes } from '../../../../lib/cart/dispensary-promo'
import {
  latestCartItemProducts$,
  setCartItemSku,
  useCartItemProducts,
} from '../../../../lib/cart/state/cart-items/cart-item-product'
import { applyRewardsState$, useApplyRewards } from '../../../../lib/cart/state/rewards'
import { useDispensaryCartStatus } from '../../../../lib/dispensaries/events/cart'
import { useLatestEScriptErrors, useLatestEScriptPromoUsed } from '../../../../lib/escript/state'
import {
  cartSubtotal,
  coloradoDeliveryFee,
  discountAmount,
  formatAsCurrencyOrZero,
  rebateAmount,
  salesTax,
  shippingTotal,
  taxType,
} from '../../../../utils/priceTools'
import Spinner from '../../../Spinner'
import PromoTotalSummarySection from '../PromoTotalSummarySection'
import TotalSummarySection from '../TotalSummarySection'
import * as Styled from './styled'

export interface OrderSummaryProps {
  cart: CartFragment
  className?: string
  totalConfirmed?: boolean
}

const RetailSubtotal: React.FC<{ skus: string[]; cart: CartFragment }> = ({ skus, cart }) => {
  const products = useCartItemProducts(skus)
  const retailSubtotal = useMemo(() => {
    if (!products || products.length === 0) {
      return 0
    }
    let total = 0
    cart.items?.map(
      (item, index) =>
        (total +=
          parseFloat(
            products[index]?.retailPrice?.amount ?? (products[index]?.price?.amount || '0'),
          ) * (item?.quantity ?? 0)),
    )

    return total
  }, [cart.items, products])

  const shouldDisplayRetailSubtotal = (cart.prices?.grand_total?.value ?? 0) < retailSubtotal

  if (!shouldDisplayRetailSubtotal) {
    return null
  }

  return (
    <Styled.RetailSubtotalSummarySection
      name="Retail Subtotal:"
      amount={formatAsCurrencyOrZero({ amount: retailSubtotal })}
    />
  )
}

const ApplyRewardsProgressContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const applyRewards = useApplyRewards()

  return applyRewards.loading ? <CircularProgress size={16} /> : <>{children}</>
}

const ApplyRewardsProgress: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Subscribe source$={applyRewardsState$} fallback={null}>
      <ApplyRewardsProgressContent>{children}</ApplyRewardsProgressContent>
    </Subscribe>
  )
}

const AppliedRewardsTooltip: React.FC = () => {
  const { featureFlags } = useSiteMetadata() ?? {}
  return (
    <Styled.RewardsTooltip placement="left">
      We&apos;ll apply store credit, rebates, and commissions, in that order. <br />
      {featureFlags?.commissionsCashBack &&
        '+10% Rewards cash-back applicable to Commissions upon invoice.'}
    </Styled.RewardsTooltip>
  )
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ className, totalConfirmed = false, cart }) => {
  const { loading } = useDispensaryCartStatus()
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const {
    applied_coupons: appliedCoupons,
    dispensary_promo_code: dispensaryPromoCode,
    prices,
  } = cart
  const skus = useMemo(
    () => cart.items?.map((item) => item?.product.sku || '').filter(Boolean) || [],
    [cart.items],
  )

  const { userCategory } = useUserCategory()
  const isPractitioner = useMemo(() => userCategory === 'practitioner', [userCategory])

  const rebateAmountText = rebateAmount(cart)
  const coloradoDeliveryFeeText = coloradoDeliveryFee(cart)

  const cartDispensaryPromoCodes = useMemo(
    () => (dispensaryPromoCode ? readCartDispensaryPromoCodes(dispensaryPromoCode) : undefined),
    [dispensaryPromoCode],
  )

  const eScriptErrors = useLatestEScriptErrors()
  const latestPromoCodeUsed = useLatestEScriptPromoUsed()

  useEffect(() => {
    cart.items?.forEach((item) => {
      if (item?.product.sku) {
        setCartItemSku(item.product.sku)
      }
    })
  }, [cart.items])

  return (
    <div className={className} data-cy="order-summary">
      {eScriptErrors && eScriptErrors.length > 0 && latestPromoCodeUsed && (
        <Styled.Alert type="informational">
          eScript Promo Code &quot;{latestPromoCodeUsed.code}&quot; could not be applied.&nbsp;
          {isPractitioner && (
            <span>This may be due to being logged in with a Practitioner account.</span>
          )}
        </Styled.Alert>
      )}
      <Styled.Heading variant="h5">ORDER SUMMARY</Styled.Heading>
      {loading ? (
        <Spinner loading />
      ) : (
        <>
          <TotalSummarySection name="Your Subtotal:" amount={cartSubtotal(cart)} />
          {shouldDisplayRetailPrice && skus.length > 0 && (
            <Subscribe source$={latestCartItemProducts$(skus)} fallback={null}>
              <RetailSubtotal skus={skus} cart={cart} />
            </Subscribe>
          )}
          <TotalSummarySection
            name="Shipping:"
            amount={shippingTotal({
              shippingAddresses: cart.shipping_addresses,
              items: cart.items,
            }).toUpperCase()}
          />
          <TotalSummarySection
            name={`Sales Tax${taxType({ prices })}:`}
            amount={salesTax({ prices })}
          />
          {coloradoDeliveryFeeText && (
            <TotalSummarySection
              name={'Colorado Retail Delivery Fee:'}
              amount={coloradoDeliveryFeeText}
            />
          )}
          <PromoTotalSummarySection
            amount={discountAmount({ appliedCoupons, prices })}
            cartDispensaryPromoCodes={cartDispensaryPromoCodes}
            coupons={appliedCoupons}
          />
          {rebateAmountText && (
            <TotalSummarySection
              name="Applied Rewards:"
              amount={<ApplyRewardsProgress>{rebateAmountText}</ApplyRewardsProgress>}
              labelEndAdornment={<AppliedRewardsTooltip />}
            />
          )}
          <Styled.TotalSection container item xs={12} justify="space-between">
            <Grid item>
              <Typography variant="subtitle2" gutterBottom>
                {totalConfirmed ? 'Total' : 'Estimated Total'}
              </Typography>
            </Grid>
            <Grid item>
              <Styled.Total>
                <ApplyRewardsProgress>
                  {formatAsCurrencyOrZero({ amount: prices?.grand_total?.value || 0 })}
                </ApplyRewardsProgress>
              </Styled.Total>
            </Grid>
          </Styled.TotalSection>
        </>
      )}
    </div>
  )
}

const StyledOrderSummary = styled(OrderSummary)({
  maxWidth: '420px',
  width: '100%',
  '.MuiTypography-body2, .MuiTypography-subtitle2': {
    fontSize: '15px',
    lineHeight: '30px',
    marginBottom: '0',
  },
})

export { StyledOrderSummary as OrderSummary }
